/**
 * Minimal store-only (method 0) ZIP container. Writes standard local headers,
 * a central directory and an end record so any unzip tool can open the file.
 * No ZIP64: entries and the archive must stay below 4 GiB.
 */

const LOCAL_SIG = 0x04034b50;
const CENTRAL_SIG = 0x02014b50;
const END_SIG = 0x06054b50;
const UTF8_FLAG = 0x0800;
const ENCRYPTED_FLAG = 0x0001;
const MAX_U32 = 0xffffffff;
const MAX_U16 = 0xffff;
const END_RECORD_SIZE = 22;

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

export function crc32Update(crc: number, bytes: Uint8Array): number {
  let c = ~crc >>> 0;
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]!) & 0xff]! ^ (c >>> 8);
  return ~c >>> 0;
}

export function crc32(bytes: Uint8Array): number {
  return crc32Update(0, bytes);
}

async function crc32OfBlob(blob: Blob): Promise<number> {
  let crc = 0;
  const reader = blob.stream().getReader();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) return crc;
    crc = crc32Update(crc, value);
  }
}

/** Entry names are untrusted input on import and must stay inside the extraction root. */
export function validateEntryName(name: string): void {
  if (name === '' || name.length > 1024) throw new Error('Archive entry has an invalid name length');
  if (name.includes('\0') || name.includes('\\')) throw new Error(`Archive entry name "${name}" contains forbidden characters`);
  if (name.startsWith('/') || /^[A-Za-z]:/.test(name)) throw new Error(`Archive entry name "${name}" is absolute`);
  for (const part of name.split('/')) {
    if (part === '..' || part === '.') throw new Error(`Archive entry name "${name}" escapes the archive root`);
  }
}

function dosDateTime(date: Date): { time: number; date: number } {
  const year = Math.min(2107, Math.max(1980, date.getFullYear()));
  return {
    time: (date.getHours() << 11) | (date.getMinutes() << 5) | (date.getSeconds() >> 1),
    date: ((year - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate(),
  };
}

export interface ZipInput {
  name: string;
  data: Uint8Array | Blob;
}

export async function createZip(entries: readonly ZipInput[], modified = new Date()): Promise<Blob> {
  if (entries.length > MAX_U16) throw new Error('Too many entries for a ZIP archive');
  const stamp = dosDateTime(modified);
  const encoder = new TextEncoder();
  const parts: BlobPart[] = [];
  const central: Uint8Array[] = [];
  const names = new Set<string>();
  let offset = 0;

  for (const entry of entries) {
    validateEntryName(entry.name);
    if (names.has(entry.name)) throw new Error(`Duplicate archive entry "${entry.name}"`);
    names.add(entry.name);
    const nameBytes = encoder.encode(entry.name);
    const size = entry.data instanceof Blob ? entry.data.size : entry.data.byteLength;
    if (size > MAX_U32) throw new Error(`Entry "${entry.name}" is too large for a ZIP archive`);
    const crc = entry.data instanceof Blob ? await crc32OfBlob(entry.data) : crc32(entry.data);

    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, LOCAL_SIG, true);
    local.setUint16(4, 10, true);
    local.setUint16(6, UTF8_FLAG, true);
    local.setUint16(8, 0, true);
    local.setUint16(10, stamp.time, true);
    local.setUint16(12, stamp.date, true);
    local.setUint32(14, crc, true);
    local.setUint32(18, size, true);
    local.setUint32(22, size, true);
    local.setUint16(26, nameBytes.length, true);
    local.setUint16(28, 0, true);

    const dir = new DataView(new ArrayBuffer(46));
    dir.setUint32(0, CENTRAL_SIG, true);
    dir.setUint16(4, 20, true);
    dir.setUint16(6, 10, true);
    dir.setUint16(8, UTF8_FLAG, true);
    dir.setUint16(10, 0, true);
    dir.setUint16(12, stamp.time, true);
    dir.setUint16(14, stamp.date, true);
    dir.setUint32(16, crc, true);
    dir.setUint32(20, size, true);
    dir.setUint32(24, size, true);
    dir.setUint16(28, nameBytes.length, true);
    dir.setUint32(42, offset, true);
    const dirBytes = new Uint8Array(46 + nameBytes.length);
    dirBytes.set(new Uint8Array(dir.buffer), 0);
    dirBytes.set(nameBytes, 46);
    central.push(dirBytes);

    parts.push(local.buffer, nameBytes, entry.data as BlobPart);
    offset += 30 + nameBytes.length + size;
    if (offset > MAX_U32) throw new Error('Archive exceeds the 4 GiB ZIP limit');
  }

  const centralSize = central.reduce((n, c) => n + c.length, 0);
  if (offset + centralSize + END_RECORD_SIZE > MAX_U32) throw new Error('Archive exceeds the 4 GiB ZIP limit');
  const end = new DataView(new ArrayBuffer(END_RECORD_SIZE));
  end.setUint32(0, END_SIG, true);
  end.setUint16(8, entries.length, true);
  end.setUint16(10, entries.length, true);
  end.setUint32(12, centralSize, true);
  end.setUint32(16, offset, true);
  parts.push(...(central as BlobPart[]), end.buffer);
  return new Blob(parts, { type: 'application/zip' });
}

export interface ZipLimits {
  maxEntries: number;
  maxEntryBytes: number;
  maxTotalBytes: number;
}

export const DEFAULT_ZIP_LIMITS: ZipLimits = {
  maxEntries: 10_000,
  maxEntryBytes: 2 * 1024 ** 3,
  maxTotalBytes: 4 * 1024 ** 3 - 1,
};

export interface ZipEntry {
  name: string;
  size: number;
  crc32: number;
  /** Returns the entry bytes as a lazily backed Blob after verifying its CRC-32. */
  blob(): Promise<Blob>;
  bytes(): Promise<Uint8Array>;
}

async function readRange(file: Blob, start: number, end: number): Promise<DataView> {
  const buf = await file.slice(start, end).arrayBuffer();
  return new DataView(buf);
}

export async function readZip(file: Blob, limits: ZipLimits = DEFAULT_ZIP_LIMITS): Promise<ZipEntry[]> {
  if (file.size < END_RECORD_SIZE) throw new Error('Not a ZIP archive (too small)');
  if (file.size > limits.maxTotalBytes) throw new Error('Archive is larger than the allowed maximum');

  const tailStart = Math.max(0, file.size - (END_RECORD_SIZE + MAX_U16));
  const tail = await readRange(file, tailStart, file.size);
  let endPos = -1;
  for (let i = tail.byteLength - END_RECORD_SIZE; i >= 0; i--) {
    if (tail.getUint32(i, true) === END_SIG) {
      const commentLen = tail.getUint16(i + 20, true);
      if (i + END_RECORD_SIZE + commentLen === tail.byteLength) {
        endPos = i;
        break;
      }
    }
  }
  if (endPos < 0) throw new Error('Not a ZIP archive (end record not found)');

  const count = tail.getUint16(endPos + 10, true);
  const dirSize = tail.getUint32(endPos + 12, true);
  const dirOffset = tail.getUint32(endPos + 16, true);
  if (tail.getUint16(endPos + 4, true) !== 0 || tail.getUint16(endPos + 6, true) !== 0) {
    throw new Error('Multi-disk ZIP archives are not supported');
  }
  if (count === MAX_U16 || dirSize === MAX_U32 || dirOffset === MAX_U32) {
    throw new Error('ZIP64 archives are not supported');
  }
  if (count > limits.maxEntries) throw new Error(`Archive has too many entries (${count})`);
  if (dirOffset + dirSize > file.size) throw new Error('ZIP central directory is out of bounds');

  const dir = await readRange(file, dirOffset, dirOffset + dirSize);
  const decoder = new TextDecoder('utf-8', { fatal: true });
  const entries: ZipEntry[] = [];
  const seen = new Set<string>();
  let total = 0;
  let pos = 0;

  for (let n = 0; n < count; n++) {
    if (pos + 46 > dir.byteLength || dir.getUint32(pos, true) !== CENTRAL_SIG) {
      throw new Error('ZIP central directory is corrupt');
    }
    const flags = dir.getUint16(pos + 8, true);
    const method = dir.getUint16(pos + 10, true);
    const crc = dir.getUint32(pos + 16, true);
    const compressed = dir.getUint32(pos + 20, true);
    const size = dir.getUint32(pos + 24, true);
    const nameLen = dir.getUint16(pos + 28, true);
    const extraLen = dir.getUint16(pos + 30, true);
    const commentLen = dir.getUint16(pos + 32, true);
    const localOffset = dir.getUint32(pos + 42, true);
    if (pos + 46 + nameLen + extraLen + commentLen > dir.byteLength) throw new Error('ZIP central directory is corrupt');

    let name: string;
    try {
      name = decoder.decode(new Uint8Array(dir.buffer, pos + 46, nameLen));
    } catch {
      throw new Error('Archive entry name is not valid UTF-8');
    }
    pos += 46 + nameLen + extraLen + commentLen;

    // Directory placeholders carry no data and are never extracted.
    if (name.endsWith('/')) continue;
    validateEntryName(name);
    if (seen.has(name)) throw new Error(`Duplicate archive entry "${name}"`);
    seen.add(name);

    if (flags & ENCRYPTED_FLAG) throw new Error(`Entry "${name}" is encrypted`);
    if (method !== 0) throw new Error(`Entry "${name}" uses compression method ${method}; only stored entries are supported`);
    if (compressed !== size) throw new Error(`Entry "${name}" has inconsistent sizes`);
    if (size > limits.maxEntryBytes) throw new Error(`Entry "${name}" exceeds the allowed size`);
    total += size;
    if (total > limits.maxTotalBytes) throw new Error('Archive contents exceed the allowed total size');

    // Sizes and CRC come from the central directory (local ones may be deferred to a data descriptor);
    // the local header is only used to find the data and cross-check the name.
    if (localOffset + 30 > file.size) throw new Error(`Entry "${name}" has an out-of-bounds header`);
    const local = await readRange(file, localOffset, localOffset + 30);
    if (local.getUint32(0, true) !== LOCAL_SIG) throw new Error(`Entry "${name}" has a corrupt local header`);
    const localNameLen = local.getUint16(26, true);
    const localExtraLen = local.getUint16(28, true);
    const nameStart = localOffset + 30;
    const dataStart = nameStart + localNameLen + localExtraLen;
    if (dataStart + size > dirOffset) throw new Error(`Entry "${name}" data overlaps the central directory`);
    const localName = new Uint8Array(await file.slice(nameStart, nameStart + localNameLen).arrayBuffer());
    if (decoder.decode(localName) !== name) throw new Error(`Entry "${name}" local and central names differ`);

    const slice = file.slice(dataStart, dataStart + size);
    const verified = async (): Promise<Blob> => {
      if ((await crc32OfBlob(slice)) !== crc) throw new Error(`Entry "${name}" failed its CRC-32 check`);
      return slice;
    };
    entries.push({
      name,
      size,
      crc32: crc,
      blob: verified,
      bytes: async () => new Uint8Array(await (await verified()).arrayBuffer()),
    });
  }
  return entries;
}
